// src-lit/components/product/kd-pantry-product-detail-modal.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';
import { STATE_CLASSES } from '../../constants/classes';

const HIDDEN_CLASS = 'tw:hidden';
const FLEX_CLASS = 'tw:flex';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @element kd-pantry-product-detail-modal
 * @description パントリー商品カードの「詳細をみる」ボタンから開く商品詳細モーダル。
 *
 *   ## 動的コンテンツ流し込み（案 A: SSR template + cloneNode）
 *   `[data-pantry-product-detail-template][data-product-id="<id>"]` テンプレートを
 *   商品分だけ事前 SSR しておき、`[data-pantry-product-detail-trigger]` クリック時に
 *   trigger の `data-product-id` で対応 template を取得し、`content.cloneNode(true)` を
 *   `[data-pantry-product-detail-content]` に挿入する。
 *
 *   docs/standards/lit-components.md「Section Rendering API は遅延レンダリング用途禁止
 *   → Storefront API + template clone を使用」に従う方式。HTTP roundtrip ゼロで開ける。
 *
 *   ## 閉じる経路
 *   ESC / オーバーレイクリック / `[data-pantry-product-detail-close]` クリック。
 *   閉じる時は CONTENT 領域を空にする（cloneNode された内部の Lit element は
 *   disconnectedCallback で listener が解除される）。
 */
@customElement('kd-pantry-product-detail-modal')
export class KdPantryProductDetailModal extends LitElement {
  createRenderRoot() {
    return this;
  }

  private previouslyFocusedElement: HTMLElement | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.querySelector(SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.OVERLAY)?.addEventListener(
      'click',
      this.handleOverlayClick,
    );
    this.addEventListener('click', this.handleCloseButtonClick);
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('click', this.handleExternalTriggerClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.querySelector(SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.OVERLAY)?.removeEventListener(
      'click',
      this.handleOverlayClick,
    );
    this.removeEventListener('click', this.handleCloseButtonClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleExternalTriggerClick);
    document.body.classList.remove(STATE_CLASSES.OVERFLOW_HIDDEN);
  }

  private handleOpen = (): void => {
    const overlay = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.OVERLAY,
    );
    if (!overlay) return;

    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    overlay.classList.remove(HIDDEN_CLASS);
    overlay.classList.add(FLEX_CLASS);
    overlay.removeAttribute('aria-hidden');
    document.body.classList.add(STATE_CLASSES.OVERFLOW_HIDDEN);

    const firstFocusable = overlay.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    firstFocusable?.focus();
  };

  private handleClose = (): void => {
    const overlay = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.OVERLAY,
    );
    if (!overlay) return;

    overlay.classList.add(HIDDEN_CLASS);
    overlay.classList.remove(FLEX_CLASS);
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(STATE_CLASSES.OVERFLOW_HIDDEN);

    // 流し込み済みコンテンツをクリア（内部の Lit element は disconnect される）。
    // 次回開く時に最新の template を必ず clone するため、ここで空にする。
    const content = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.CONTENT,
    );
    if (content) content.replaceChildren();

    this.previouslyFocusedElement?.focus();
    this.previouslyFocusedElement = null;
  };

  private handleCloseButtonClick = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (target.closest(SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.CLOSE)) {
      this.handleClose();
    }
  };

  private handleOverlayClick = (e: Event): void => {
    if (e.target === this.querySelector(SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.OVERLAY)) {
      this.handleClose();
    }
  };

  private handleKeyDown = (e: KeyboardEvent): void => {
    const overlay = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.OVERLAY,
    );
    if (!overlay || overlay.classList.contains(HIDDEN_CLASS)) return;

    if (e.key === 'Escape') {
      this.handleClose();
    } else if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  };

  private trapFocus = (e: KeyboardEvent): void => {
    const overlay = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.OVERLAY,
    );
    if (!overlay) return;

    const focusable = Array.from(
      overlay.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.closest('[hidden]'));
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /**
   * カード内の「詳細をみる」ボタンクリックを document レベルで監視。
   *
   * 同一ページに本要素が複数存在する場合、document 順で最初の 1 つだけが処理する
   * （クリック毎にチェックすることで、primary が disconnect → 別 instance が
   * 残るケースでも自動的に新しい先頭 instance が引き継げる）。
   *
   * trigger の `data-product-id` を読み取り、対応する `<template>` を clone して
   * CONTENT に流し込んでから handleOpen() する。template / content が
   * 見つからない場合は何もせず黙って終了する（誤クリック相当）。
   */
  private handleExternalTriggerClick = (e: Event): void => {
    if (document.querySelector(SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.ELEMENT) !== this) return;

    const target = e.target as HTMLElement;
    if (!target) return;

    const trigger = target.closest<HTMLElement>(
      SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.EXTERNAL_TRIGGER,
    );
    if (!trigger) return;

    const productId = trigger.dataset.productId;
    if (!productId) return;

    // Shopify の product.id は数値始まり（例: "7654321098765"）のため、CSS セレクタの
    // 属性値に埋め込むと CSS.escape が `\37 ...` 形式に変換して HTML 属性値と
    // マッチしなくなる。JS 側で dataset を比較する方式が最も安全。
    const template =
      Array.from(
        this.querySelectorAll<HTMLTemplateElement>(
          SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.TEMPLATE,
        ),
      ).find((el) => el.dataset.productId === productId) ?? null;
    const content = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_PRODUCT_DETAIL_MODAL.CONTENT,
    );
    if (!template || !content) return;

    content.replaceChildren(template.content.cloneNode(true));
    this.handleOpen();
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-pantry-product-detail-modal': KdPantryProductDetailModal;
  }
}
